import { useState, useRef, useEffect } from 'react';
import { useWakeLock } from '../hooks/useWakeLock';
import { TimerAudioManager } from '../components/timer/TimerAudioManager';
import './HIITPage.css';
import './TimerStopwatch.css';

const REST_SECS = 60; // fixed 1-minute rest between rounds
const ROUND_OPTIONS = [2, 3, 5]; // minutes per round

function formatTime(seconds) {
  const s = Math.max(0, seconds);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function calcTotalMinutes(roundMins, rounds) {
  const totalSecs = rounds * roundMins * 60 + (rounds - 1) * REST_SECS;
  return Math.ceil(totalSecs / 60);
}

/**
 * Phase transition:  fight → rest (if not last round) → fight
 *                    fight → done (last round, no rest after final bell)
 */
function advancePhase({ phase, curRound, rounds, fightSecs }) {
  if (phase === 'fight') {
    if (curRound >= rounds) return { done: true };
    return { phase: 'rest', curRound, timeLeft: REST_SECS, done: false };
  }
  // phase === 'rest'
  const nextRound = curRound + 1;
  return { phase: 'fight', curRound: nextRound, timeLeft: fightSecs, done: false };
}

export default function KampfsportPage() {
  // ── Config state ──────────────────────────────────────────────────────────
  const [roundMins,     setRoundMins]     = useState(null); // 2 | 3 | 5
  const [rounds,        setRounds]        = useState(1);
  const [soundsEnabled, setSoundsEnabled] = useState(true);

  // ── Timer state ───────────────────────────────────────────────────────────
  const [view,     setView]     = useState('config'); // config|countdown|running|paused|done
  const [phase,    setPhase]    = useState('fight');  // fight|rest
  const [curRound, setCurRound] = useState(1);
  const [timeLeft, setTimeLeft] = useState(0);
  const [cdNum,    setCdNum]    = useState(3);
  const [runKey,   setRunKey]   = useState(0);

  // Refs for interval closures
  const intervalRef          = useRef(null);
  const timeLeftRef          = useRef(0);
  const phaseRef             = useRef('fight');
  const curRoundRef          = useRef(1);
  const roundMinsRef         = useRef(null);
  const roundsRef            = useRef(1);
  const phaseEndTimeRef      = useRef(null);
  const lastBeepSecRef       = useRef(null);
  const boxCounterFiredRef   = useRef(false);
  const pausedRemainingMsRef = useRef(null);
  const animFrameRef         = useRef(null);
  const progressBarRef       = useRef(null);

  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = new TimerAudioManager();
  const soundsEnabledRef = useRef(soundsEnabled);

  useEffect(() => { soundsEnabledRef.current = soundsEnabled; }, [soundsEnabled]);
  useEffect(() => { roundMinsRef.current = roundMins; }, [roundMins]);
  useEffect(() => { roundsRef.current = rounds; }, [rounds]);
  useEffect(() => () => audioRef.current?.destroy(), []);

  useWakeLock(view === 'running');

  // ── Countdown 3-2-1 ───────────────────────────────────────────────────────
  useEffect(() => {
    if (view !== 'countdown') return;
    let count = 3;
    setCdNum(3);
    if (soundsEnabledRef.current) audioRef.current.playCountdownBeep();
    const id = setInterval(() => {
      count--;
      if (count > 0) {
        setCdNum(count);
        if (soundsEnabledRef.current) audioRef.current.playCountdownBeep();
      } else {
        clearInterval(id);
        const fightSecs = roundMinsRef.current * 60;
        phaseRef.current        = 'fight';
        curRoundRef.current     = 1;
        timeLeftRef.current     = fightSecs;
        phaseEndTimeRef.current  = Date.now() + fightSecs * 1000;
        lastBeepSecRef.current   = null;
        boxCounterFiredRef.current = false;
        if (soundsEnabledRef.current) audioRef.current.playBellStart();
        setPhase('fight');
        setCurRound(1);
        setTimeLeft(fightSecs);
        setView('running');
      }
    }, 1000);
    return () => clearInterval(id);
  }, [view]);

  // ── Running timer (200ms wall-clock polling) ──────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (view !== 'running') {
      clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      const remainingMs = phaseEndTimeRef.current - Date.now();
      if (remainingMs <= 0) {
        const result = advancePhase({
          phase:    phaseRef.current,
          curRound: curRoundRef.current,
          rounds:   roundsRef.current,
          fightSecs: roundMinsRef.current * 60,
        });
        // Buzzer at end of every fight round (including last)
        if (phaseRef.current === 'fight' && soundsEnabledRef.current) {
          audioRef.current.playBuzzer();
        }
        if (result.done) {
          clearInterval(intervalRef.current);
          setView('done');
        } else {
          phaseRef.current        = result.phase;
          curRoundRef.current     = result.curRound;
          timeLeftRef.current     = result.timeLeft;
          phaseEndTimeRef.current    = Date.now() + result.timeLeft * 1000;
          lastBeepSecRef.current     = null;
          boxCounterFiredRef.current = false;
          if (result.phase === 'fight' && soundsEnabledRef.current) {
            audioRef.current.playBellStart();
          }
          setPhase(result.phase);
          setCurRound(result.curRound);
          setTimeLeft(result.timeLeft);
        }
      } else {
        const secs = Math.ceil(remainingMs / 1000);
        timeLeftRef.current = secs;
        setTimeLeft(secs);
        // Countdown beeps in last 10s of rest
        if (soundsEnabledRef.current && phaseRef.current === 'rest' && secs <= 10 && secs !== lastBeepSecRef.current) {
          lastBeepSecRef.current = secs;
          audioRef.current.playCountdownBeep();
        }
        // Box counter once when 10s remain in fight round
        if (soundsEnabledRef.current && phaseRef.current === 'fight' && secs <= 10 && !boxCounterFiredRef.current) {
          boxCounterFiredRef.current = true;
          audioRef.current.playBoxCounter();
        }
      }
    }, 200);
    return () => clearInterval(intervalRef.current);
  }, [view, runKey]);

  // ── Smooth progress bar via requestAnimationFrame ─────────────────────────
  useEffect(() => {
    if (view !== 'running') {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
      return;
    }
    function rafTick() {
      if (!progressBarRef.current) return;
      const remainingMs = Math.max(0, phaseEndTimeRef.current - Date.now());
      const totalMs = (phaseRef.current === 'fight' ? roundMinsRef.current * 60 : REST_SECS) * 1000;
      const pct = totalMs > 0 ? Math.min(100, (remainingMs / totalMs) * 100) : 0;
      progressBarRef.current.style.width = `${pct}%`;
      animFrameRef.current = requestAnimationFrame(rafTick);
    }
    animFrameRef.current = requestAnimationFrame(rafTick);
    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
    };
  }, [view, runKey]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  function handleStart() {
    audioRef.current.unlock();
    setView('countdown');
  }

  function handlePause() {
    pausedRemainingMsRef.current = Math.max(0, phaseEndTimeRef.current - Date.now());
    clearInterval(intervalRef.current);
    setView('paused');
  }

  function handleResume() {
    phaseEndTimeRef.current = Date.now() + (pausedRemainingMsRef.current ?? timeLeftRef.current * 1000);
    pausedRemainingMsRef.current = null;
    lastBeepSecRef.current = null;
    setView('running');
  }

  function handleReset() {
    clearInterval(intervalRef.current);
    setView('countdown');
  }

  function handleSkip() {
    clearInterval(intervalRef.current);
    const result = advancePhase({
      phase:    phaseRef.current,
      curRound: curRoundRef.current,
      rounds:   roundsRef.current,
      fightSecs: roundMinsRef.current * 60,
    });
    if (phaseRef.current === 'fight' && soundsEnabledRef.current) {
      audioRef.current.playBuzzer();
    }
    if (result.done) {
      setView('done');
    } else {
      phaseRef.current        = result.phase;
      curRoundRef.current     = result.curRound;
      timeLeftRef.current     = result.timeLeft;
      phaseEndTimeRef.current    = Date.now() + result.timeLeft * 1000;
      lastBeepSecRef.current     = null;
      boxCounterFiredRef.current = false;
      if (result.phase === 'fight' && soundsEnabledRef.current) {
        audioRef.current.playBellStart();
      }
      setPhase(result.phase);
      setCurRound(result.curRound);
      setTimeLeft(result.timeLeft);
      if (view === 'running') {
        setRunKey((k) => k + 1);
      } else {
        setView('running');
      }
    }
  }

  function handleAbort() {
    clearInterval(intervalRef.current);
    setView('config');
  }

  // ── Derived values ────────────────────────────────────────────────────────
  const isTimerMode  = view !== 'config';
  const isFight      = phase === 'fight';
  const phaseColor   = isFight ? '#ef4444' : '#60a5fa';
  const timerBg      = view === 'running' || view === 'paused'
    ? (isFight ? '#1a0d0d' : '#0d1e33')
    : '#1a2332';
  const [timerMins, timerSecs] = formatTime(timeLeft).split(':');

  return (
    <div
      className={isTimerMode ? 'hiit-fullscreen' : undefined}
      style={isTimerMode ? { background: timerBg } : undefined}
      data-theme={isTimerMode ? 'dark' : undefined}
      data-bs-theme={isTimerMode ? 'dark' : undefined}
    >

      {/* ── CONFIG ─────────────────────────────────────────────────────── */}
      {view === 'config' && (
        <>
          <h4 className="mb-4" style={{ color: 'var(--color-text)', fontWeight: 700 }}>
            <i className="bi bi-trophy me-2" />
            Kampfsport Quick Start
          </h4>

          <div className="tools-card mb-4">
            <div className="hiit-section-label">Rundendauer wählen</div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10,
                marginBottom: 20,
              }}
            >
              {ROUND_OPTIONS.map((min) => (
                <button
                  key={min}
                  className="timer-history-tile"
                  style={{
                    padding: '20px 8px',
                    fontSize: '1.35rem',
                    fontWeight: 700,
                    color: roundMins === min ? 'var(--color-accent)' : 'var(--color-text)',
                    border: roundMins === min ? '2px solid var(--color-accent)' : '2px solid var(--color-border)',
                    minHeight: 72,
                  }}
                  onClick={() => setRoundMins(min)}
                >
                  {min} min
                </button>
              ))}
            </div>

            <div className="hiit-section-label">Anzahl Runden</div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 20,
                marginBottom: 20,
              }}
            >
              <button
                className="tools-btn-ghost"
                style={{ fontSize: '1.4rem', padding: '4px 16px', lineHeight: 1 }}
                onClick={() => setRounds((r) => Math.max(1, r - 1))}
              >
                −
              </button>
              <span style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-accent)', minWidth: 48, textAlign: 'center' }}>
                {rounds}
              </span>
              <button
                className="tools-btn-ghost"
                style={{ fontSize: '1.4rem', padding: '4px 16px', lineHeight: 1 }}
                onClick={() => setRounds((r) => Math.min(20, r + 1))}
              >
                +
              </button>
            </div>

            {roundMins && (
              <div className="hiit-summary-chips">
                {[
                  { label: 'Rundendauer', val: `${roundMins} min` },
                  { label: 'Runden',      val: rounds },
                  { label: 'Pause',       val: '1 min' },
                  { label: 'Gesamt ca.', val: `${calcTotalMinutes(roundMins, rounds)} min` },
                ].map(({ label, val }) => (
                  <div key={label} className="hiit-sum-chip">
                    <div className="hiit-sum-chip-label">{label}</div>
                    <div className="hiit-sum-chip-val">{val}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="form-check mt-3 mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="kampf_sounds_enabled"
                checked={soundsEnabled}
                onChange={(e) => setSoundsEnabled(e.target.checked)}
              />
              <label className="form-check-label hiit-sounds-label" htmlFor="kampf_sounds_enabled">
                <i className="bi bi-volume-up me-1" />
                Töne aktivieren (Buzzer &amp; Beeps)
              </label>
            </div>

            <button
              className="workout-control-btn-fullwidth"
              disabled={!roundMins}
              onClick={handleStart}
            >
              <i className="bi bi-play-fill" />
              Workout starten
            </button>
          </div>
        </>
      )}

      {/* ── COUNTDOWN ──────────────────────────────────────────────────── */}
      {view === 'countdown' && (
        <>
          <div className="workout-control-topbar">
            <div className="workout-control-topbar__slot">
              <button className="workout-control-btn-icon-sm" onClick={handleAbort} aria-label="Workout abbrechen">
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="workout-control-topbar__slot workout-control-topbar__slot--center"></div>
            <div className="workout-control-topbar__slot"></div>
          </div>
          <div className="workout-control-content">
            <div className="hiit-countdown">
              <div className="hiit-countdown-label">Bereit machen</div>
              <div className="hiit-countdown-num">{cdNum}</div>
              <div className="hiit-countdown-cfg">
                {roundMins} min · {rounds} Runden · 1 min Pause
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── RUNNING / PAUSED ───────────────────────────────────────────── */}
      {(view === 'running' || view === 'paused') && (
        <>
          <div className="workout-control-topbar">
            <div className="workout-control-topbar__slot">
              <button className="workout-control-btn-icon-sm" onClick={handleAbort} aria-label="Workout abbrechen">
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="workout-control-topbar__slot workout-control-topbar__slot--center">
              <span className={`workout-control-phase-pill workout-control-phase-pill--${
                isFight ? 'work' : 'rest'
              }`}>
                {isFight ? 'KAMPF' : 'PAUSE'}
              </span>
            </div>
            <div className="workout-control-topbar__slot"></div>
          </div>

          <div className="workout-control-content">
            <div className="hiit-timer">
              <div
                className="hiit-phase-badge"
                style={{
                  background: isFight ? 'rgba(239,68,68,0.15)' : 'rgba(96,165,250,0.15)',
                  color: phaseColor,
                }}
              >
                {isFight ? 'KAMPF' : 'PAUSE'}
              </div>

              <div className="hiit-round-info">Runde {curRound} von {rounds}</div>

              <div className="hiit-timer-display" style={{ color: phaseColor }}>
                <span className="hiit-timer-segment">{timerMins}</span>
                <span className="hiit-timer-colon" style={{ color: phaseColor }}>:</span>
                <span className="hiit-timer-segment">{timerSecs}</span>
              </div>

              <div
                className="hiit-phase-label"
                style={{ color: isFight ? 'rgba(239,68,68,0.55)' : 'rgba(96,165,250,0.55)' }}
              >
                {isFight ? 'Kampfphase' : 'Erholung'}
              </div>

              <div className="hiit-progress-wrap">
                <div className="hiit-progress-track">
                  <div
                    className="hiit-progress-fill"
                    ref={progressBarRef}
                    style={{ background: phaseColor }}
                  />
                </div>
                <div className="hiit-progress-labels">
                  <span>{isFight ? 'Kampfphase' : 'Erholungsphase'}</span>
                  <span>Runde {curRound}/{rounds}</span>
                </div>
              </div>

              <div className="hiit-rounds-dots">
                {Array.from({ length: rounds }, (_, i) => (
                  <span
                    key={i}
                    className="hiit-rdot"
                    style={{
                      background:
                        i + 1 < curRound  ? '#ef4444'
                        : i + 1 === curRound ? 'var(--color-accent)'
                        : 'rgba(255,255,255,0.15)',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="workout-control-bottombar">
            <button className="workout-control-btn-secondary" onClick={handleReset} aria-label="Reset">
              <i className="bi bi-arrow-counterclockwise" />
            </button>
            <button
              className={`workout-control-btn-primary ${view === 'paused' ? 'is-paused' : 'is-playing'}`}
              onClick={view === 'paused' ? handleResume : handlePause}
              aria-label={view === 'paused' ? 'Weiter' : 'Pause'}
            >
              <span className="workout-control-btn-primary__icon workout-control-btn-primary__icon--pause">
                <i className="bi bi-pause-fill" />
              </span>
              <span className="workout-control-btn-primary__icon workout-control-btn-primary__icon--play">
                <i className="bi bi-play-fill" />
              </span>
            </button>
            <button className="workout-control-btn-secondary" onClick={handleSkip} aria-label="Überspringen">
              <i className="bi bi-skip-end-fill" />
            </button>
          </div>
        </>
      )}

      {/* ── DONE ───────────────────────────────────────────────────────── */}
      {view === 'done' && (
        <div className="hiit-done">
          <div className="hiit-done-icon">
            <svg
              width="28" height="28" viewBox="0 0 24 24"
              fill="none" stroke="#fff" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="20,6 9,17 4,12" />
            </svg>
          </div>
          <div className="hiit-done-title">Workout abgeschlossen!</div>
          <div className="hiit-done-sub">Gut gemacht</div>
          <div className="hiit-done-stats">
            {[
              { val: `${roundMins} min`,                           label: 'Rundendauer' },
              { val: rounds,                                       label: 'Runden' },
              { val: '1 min',                                      label: 'Pause' },
              { val: `${calcTotalMinutes(roundMins, rounds)} min`, label: 'Gesamt' },
            ].map(({ val, label }) => (
              <div key={label} className="hiit-done-stat">
                <div className="hiit-done-stat-val">{val}</div>
                <div className="hiit-done-stat-label">{label}</div>
              </div>
            ))}
          </div>
          <div className="d-flex flex-column gap-3" style={{ maxWidth: 480, margin: '0 auto', padding: '0 16px' }}>
            <button className="workout-control-btn-fullwidth" onClick={handleReset}>
              <i className="bi bi-arrow-clockwise" />
              Erneut starten
            </button>
            <button className="workout-control-btn-fullwidth workout-control-btn-fullwidth--ghost" onClick={handleAbort}>
              Zur Übersicht
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
